import {defineStore} from "pinia";
import {useApiClient} from "@/hooks/api-hook.ts";
import {computed, ref} from "vue";
import {User} from "@/api/client/models/index.ts";
import {AllotmentRolesConstants, TourOperatorRolesConstants} from "@/stores/definitions.js";
import {useCurrentAppStore} from "@/stores/current-app-store.ts";
import {isFeatureEnabled} from "@/config/feature-flags.ts";

export const useCurrentUserStore = defineStore('current-user', () => {
  const api = useApiClient()
  const currentAppStore = useCurrentAppStore()

  const currentUser = ref<User | null>(null)
  const isFetching = ref(false)
  let pendingRequest: Promise<User | undefined> | null = null

  const role = computed(() => currentUser.value?.allotmentRoleId)

  const tourOperatorRole = computed(() => currentUser.value?.tourOperatorRoleId)
  const isAdmin = computed(() => currentUser.value?.allotmentRoleId === AllotmentRolesConstants.ADMIN)
  const isReadonlyAdmin = computed(() => currentUser.value?.allotmentRoleId === AllotmentRolesConstants.READONLY_ADMIN)
  const isTourOperatorAllAccess = computed(() => currentUser.value?.tourOperatorRoleId === TourOperatorRolesConstants.ALL_ACCESS)
  const isTourOperatorOnlyReporting = computed(() => currentUser.value?.tourOperatorRoleId === TourOperatorRolesConstants.ONLY_REPORTING)
  const isTourOperatorOnlyAllotmentChanges = computed(() => currentUser.value?.tourOperatorRoleId === TourOperatorRolesConstants.ONLY_ALLOTMENT_CHANGES)
  const isTourOperator = computed(() => currentUser.value?.allotmentRoleId === AllotmentRolesConstants.TOUR_OPERATOR)
  const isHccReadonly = computed(() => currentUser.value?.allotmentRoleId === AllotmentRolesConstants.HCC_READONLY)
  const isFinance = computed(() => currentUser.value?.allotmentRoleId === AllotmentRolesConstants.FINANCE)
  const hasCharterAccess = computed(() => isFeatureEnabled("charterApp") && !!currentUser.value?.charterRoleId)

  const profile = computed(() => currentUser.value)

  async function fetchCurrentUser() {
    if (pendingRequest) {
      return pendingRequest
    }

    isFetching.value = true

    pendingRequest = api
      .user
      .current
      .get()
      .then(response => {
        currentUser.value = response ?? null
        currentAppStore.setInitialCurrentAppFromUser(response)
        return response
      })
      .finally(() => {
        isFetching.value = false
        pendingRequest = null
      })

    return pendingRequest
  }

  async function get() {
    return fetchCurrentUser()
  }

  async function ensureLoaded() {
    if (currentUser.value) {
      return currentUser.value
    }

    return fetchCurrentUser()
  }

  return {
    get,
    ensureLoaded,
    currentUser,
    isFetching,
    isAdmin,
    isReadonlyAdmin,
    isTourOperatorAllAccess,
    isTourOperator,
    isHccReadonly,
    role,
    isTourOperatorOnlyReporting,
    isTourOperatorOnlyAllotmentChanges,
    isFinance,
    tourOperatorRole,
    hasCharterAccess,
    //TODO: remove profile when all references are gone
    profile
  }
})
